package navigation.home.allitinerary

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.ui.AddEditClientDialog
import com.essntl.features.client.ui.addeditclient.AddEditClientEvent
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.itinerary.ui.all_itinerary.AllItineraryEvent
import com.essntl.features.itinerary.ui.all_itinerary.AllItineraryNavigation
import com.essntl.features.itinerary.ui.all_itinerary.AllItineraryScreen
import com.essntl.features.itinerary.ui.itinerary_overview.ItineraryOverviewEvent
import com.essntl.features.itinerary.ui.itinerary_overview.ItineraryOverviewNavigation
import com.essntl.features.itinerary.ui.itinerary_overview.ItineraryOverviewScreen
import essntl.key.LocalKeyEventManager
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.flow.collectLatest
import navigation.home.clients.addeditclient.AddEditClientScreenModel
import navigation.home.edititinerary.EditItineraryScreenNav
import navigation.home.invoice.addeditinvoice.AddEditInvoiceScreenNav
import navigation.home.invoice.allinvoice.AllInvoiceScreenModel
import navigation.home.itineraryoverview.ItineraryOverviewScreenModel
import navigation.utils.NavLinkScreen
import navigation.utils.safePush

object AllItineraryScreenNav : NavLinkScreen {
    override val defaultTitle: String = "Itineraries"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Itinerary,
        ),
    )

    @Composable
    override fun Content() {
        val allItineraryScreenModel = koinScreenModel<AllItineraryScreenModel>()
        val allItineraryState by allItineraryScreenModel.state.collectAsState()

        val invoiceScreenModel = koinScreenModel<AllInvoiceScreenModel>()
        val invoiceState by invoiceScreenModel.state.collectAsState()

        val itineraryOverviewScreenModel = koinScreenModel<ItineraryOverviewScreenModel>()
        val itineraryOverviewState by itineraryOverviewScreenModel.state.collectAsState()

        var openAddEditClientDialog by rememberSaveable { mutableStateOf(false) }
        var clientToEdit by remember { mutableStateOf<ClientModel?>(null) }
        var itineraryToEdit by remember { mutableStateOf<ItineraryModel?>(null) }

        val addEditClientScreenModel = koinScreenModel<AddEditClientScreenModel>()
        val addEditClientState by addEditClientScreenModel.state.collectAsState()

        val navigator = LocalNavigator.currentOrThrow

        val keyEventManager = LocalKeyEventManager.current

        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        LaunchedEffect(Unit) {
            allItineraryScreenModel.onEvent(AllItineraryEvent.LoadAllItinerary)
            navLinkState = navLinkState.copy(
                onRefresh = {
                    allItineraryScreenModel.onEvent(AllItineraryEvent.LoadAllItinerary)
                    sidePanelState.sidePanelOpened = false
                },
            )
        }

        LaunchedEffect(keyEventManager) {
            keyEventManager.events.collectLatest {
                if (it.key == Key.H && it.type == KeyEventType.KeyUp) {
                    sidePanelState.sidePanelOpened = false
                }
            }
        }

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened) {
                itineraryOverviewScreenModel.onEvent(ItineraryOverviewEvent.ClearState)
            }
        }

        LaunchedEffect(itineraryOverviewState.getItinerary.data) {
            allItineraryScreenModel.onEvent(
                AllItineraryEvent.SelectItinerary(
                    itineraryId = itineraryOverviewState.getItinerary.data?.id,
                ),
            )
        }

        LaunchedEffect(addEditClientState.addEditClient.isSuccess) {
            if (addEditClientState.addEditClient.isSuccess) {
                addEditClientState.addEditClient.data?.let {
                    itineraryToEdit?.let { i ->
                        clientToEdit?.let { c ->
                            allItineraryScreenModel.onEvent(
                                AllItineraryEvent.EditClient(
                                    client = it,
                                    itinerary = i,
                                ),
                            )
                        }
                    }
                }

                addEditClientScreenModel.onEvent(AddEditClientEvent.ClearClientState)
                openAddEditClientDialog = false
                clientToEdit = null
                itineraryToEdit = null
            }
        }

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .padding(end = 1.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius)),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier =
                            Modifier
                                .background(MaterialTheme.esColorScheme.bgSecondary)
                                .padding(2.5f.spacing)
                                .fillMaxWidth(),
                    ) {
                        EsMediumOutlineIconButton(
                            onClick = {
                                sidePanelState.sidePanelOpened = false
                            },
                            iconRes = EsIcons.ArrowRight,
                        )

                        Spacer(modifier = Modifier.width(2.5f.spacing))

                        SelectionContainer {
                            Text(
                                text = itineraryOverviewState.getItinerary.data?.proposal?.title ?: "",
                                color = MaterialTheme.esColorScheme.textPrimary,
                                style = MaterialTheme.esTypography.h3Bold24,
                                overflow = TextOverflow.Ellipsis,
                                maxLines = 1,
                                textAlign = TextAlign.End,
                                modifier = Modifier.weight(1f),
                            )
                        }
                    }

                    ItineraryOverviewScreen(
                        state = itineraryOverviewState,
                        invoiceState = invoiceState,
                        onInvoiceEvent = invoiceScreenModel::onEvent,
                        onNavigation = { event ->
                            when (event) {
                                is ItineraryOverviewNavigation.EditItinerary -> {
                                    navigator.safePush(
                                        EditItineraryScreenNav(
                                            itineraryId = event.itineraryId,
                                        ),
                                    )
                                }

                                is ItineraryOverviewNavigation.EditInvoice -> {
                                    navigator.safePush(
                                        AddEditInvoiceScreenNav(
                                            proposalId = event.proposalId,
                                            invoiceId = event.invoiceId,
                                        ),
                                    )
                                }

                                is ItineraryOverviewNavigation.AddInvoice -> {
                                    navigator.safePush(
                                        AddEditInvoiceScreenNav(
                                            proposalId = event.proposalId,
                                        ),
                                    )
                                }
                            }
                        },
                    )
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier =
                                Modifier
                                    .width(1.spacing)
                                    .fillMaxHeight(),
                        )
                    },
                ),
            modifier =
                Modifier
                    .fillMaxSize(),
        ) {
            AllItineraryScreen(
                state = allItineraryState,
                onEvent = allItineraryScreenModel::onEvent,
                onNavigation = { event ->
                    when (event) {
                        is AllItineraryNavigation.Overview -> {
                            sidePanelState.sidePanelOpened = true
                            itineraryOverviewScreenModel.onEvent(
                                ItineraryOverviewEvent.GetItinerary(event.itineraryId),
                            )
                        }

                        is AllItineraryNavigation.EditItinerary -> {
                            navigator.safePush(
                                EditItineraryScreenNav(
                                    itineraryId = event.itineraryId,
                                ),
                            )
                        }

                        is AllItineraryNavigation.EditClient -> {
                            clientToEdit = event.client
                            itineraryToEdit = event.itinerary
                            openAddEditClientDialog = true
                        }
                    }
                },
            )

            AddEditClientDialog(
                openDialog = openAddEditClientDialog,
                currentClient = clientToEdit,
                state = addEditClientState,
                validation = addEditClientScreenModel.validation,
                onEvent = addEditClientScreenModel::onEvent,
                onDismissRequest = {
                    openAddEditClientDialog = false
                    clientToEdit = null
                    itineraryToEdit = null
                },
            )
        }
    }
}
